import React, { useState, useEffect } from 'react';
import { AttributeFacet, BasicFacet, BasicFacetValue } from "../types";
import { RangeSlider } from './RangeSlider';
import { SingleValueSlider } from './SingleValueSlider';
import { AvailabilityFacetComponent } from './AvailabilityFacetComponent';
import { MarkerFacetComponent } from './MarkerFacetComponent';
import { AkcijaFacetComponent } from './AkcijaFacetComponent';

interface BasicFacetComponentProps {
  facet: BasicFacet;
  facetKey: string;
  isExpanded?: boolean;
  selectedFilters: Record<string, Set<string>>;
  onFilterChange: (
    facetKey: string,
    value: BasicFacetValue,
    checked: boolean,
    type: string | null
  ) => void;
  sanitizeFilterValue: (value: string) => string;
  isFilterSelected: (
    facet: BasicFacet | AttributeFacet,
    value: BasicFacetValue
  ) => boolean;
}

export const BasicFacetComponent: React.FC<BasicFacetComponentProps> = ({
  facet,
  facetKey,
  isExpanded = true,
  selectedFilters,
  onFilterChange,
  sanitizeFilterValue,
  isFilterSelected,
}) => {
  const [sliderRange, setSliderRange] = useState<[number, number]>([0, 100]);

  const [showCount, setShowCount] = useState<boolean>(false);
  facet.values = facet.values.sort((a, b) => a.filter.localeCompare(b.filter));

  // Initialize slider values from facet data if numeric
  useEffect(() => {
    if (
      facet.isNumber &&
      facet.values.length > 0 &&
      !Boolean(facet.range_select)
    ) {
      const numericValues = facet.values
        .map((item) => parseFloat(item.filter))
        .filter((value) => !isNaN(value));

      if (numericValues.length > 0) {
        const min = Math.min(...numericValues);
        const max = Math.max(...numericValues);
        setSliderRange([min, max]);
      }
    }
    setShowCount(facet.showCount);
  }, [facet]);

  // Handle range slider change
  const handleRangeSliderChange = (min: number, max: number) => {
    setSliderRange([min, max]);

    // Create a synthetic BasicFacetValue for the range
    const rangeValue: BasicFacetValue = {
      count: 0,
      filter: `${min}-${max}`,
      indexValue: `${min}-${max}`,
    };

    // Call the parent's filter change handler
    onFilterChange(facetKey, rangeValue, true, facet.type);
  };

  // Handle single value slider change
  const handleSingleValueSliderChange = (value: number) => {
    // setSingleValue(value);

    // Create a synthetic BasicFacetValue for the single value
    const singleValue: BasicFacetValue = {
      count: 0,
      filter: `${value}`,
      indexValue: `${value}`,
    };

    // Call the parent's filter change handler
    onFilterChange(facetKey, singleValue, true, facet.type);
  };

  if (!isExpanded) {
    return null;
  }

  // If this is a numeric facet, render the appropriate slider
  if (facet.isNumber && facet.range && !facet.range_select) {
    return (
      <div className="space-y-1">
        {
          <RangeSlider
            min={sliderRange[0]}
            max={sliderRange[1]}
            initialMin={sliderRange[0]}
            initialMax={sliderRange[1]}
            suffix={facet.suffix}
            onChange={handleRangeSliderChange}
          />
        }
      </div>
    );
  }
  if (facet.isNumber && facet.range_select) {
    return (
      <div className="ml-2 space-y-1">
        {
          <SingleValueSlider
            facet={facet}
            suffix={facet.suffix}
            isExpanded={isExpanded}
            onFilterChange={handleSingleValueSliderChange}
            isFilterSelected={isFilterSelected}
          />
        }
      </div>
    );
  }

  if (facetKey === "raspolozivo") {
    return (
      <div className="ml-2 space-y-1">
        <AvailabilityFacetComponent
          facet={facet}
          facetKey={facetKey}
          isExpanded={isExpanded}
          selectedFilters={selectedFilters}
          onFilterChange={onFilterChange}
          sanitizeFilterValue={sanitizeFilterValue}
          isFilterSelected={isFilterSelected}
        />
      </div>
    );
  }

  if (facetKey === "novo_best_price") {
    return (
      <div className="ml-2 space-y-1">
        <MarkerFacetComponent
          facet={facet}
          facetKey={facetKey}
          isExpanded={isExpanded}
          selectedFilters={selectedFilters}
          onFilterChange={onFilterChange}
          sanitizeFilterValue={sanitizeFilterValue}
          isFilterSelected={isFilterSelected}
        />
      </div>
    );
  }

  if (facetKey === "akcija") {
    return (
      <div className="ml-2 space-y-1">
        <AkcijaFacetComponent
          facet={facet}
          facetKey={facetKey}
          isExpanded={isExpanded}
          selectedFilters={selectedFilters}
          onFilterChange={onFilterChange}
          sanitizeFilterValue={sanitizeFilterValue}
          isFilterSelected={isFilterSelected}
        />
      </div>
    );
  }
  // For non-numeric facets, render checkboxes
  const [showAll, setShowAll] = React.useState(false);
  const itemsToShow = showAll ? facet.values : facet.values?.slice(0, 8);
  const hasMore = facet.values && facet.values.length > 8;

  return (
    <div className="ml-2 space-y-1">
      {itemsToShow?.map((item: BasicFacetValue) => {
        const sanitizedFilter = sanitizeFilterValue(item.filter);
        return (
          <div key={item.filter} className="flex items-top">
            <input
              type="checkbox"
              id={`${facetKey}-${sanitizedFilter}`}
              checked={isFilterSelected(facet, item)}
              onChange={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onFilterChange(facetKey, item, e.target.checked, facet.type);
              }}
              className="mr-2 h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            <label
              htmlFor={`${facetKey}-${sanitizedFilter}`}
              className="text-sm text-gray-700 cursor-pointer flex items-top justify-between w-full"
            >
              <span>{sanitizedFilter}</span>
              {showCount && (
                <span className="text-xs text-gray-500 ml-1">
                  ({item.count})
                </span>
              )}
            </label>
          </div>
        );
      })}
      {hasMore && (
        <button
          type="button"
          onClick={() => setShowAll(!showAll)}
          className="text-sm text-blue-600 hover:text-blue-800 font-medium mt-2"
        >
          {showAll
            ? "Prikaži manje"
            : `Prikaži više (${facet.values.length - 8})`}
        </button>
      )}
    </div>
  );
};
